/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, {
  useState,
  KeyboardEvent,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box, TextField, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChatInputProps } from './types';
import { Send } from 'lucide-react';
import StopIcon from '@mui/icons-material/Stop';
import { keyframes } from '@mui/material/styles';

const pulseAnimation = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
`;

const AnimatedStopIcon = styled(StopIcon)(({ theme }) => ({
  animation: `${pulseAnimation} 1s ease-in-out infinite`,
  color: theme.palette.error.main,
}));

const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.grey[50],
    borderRadius: 24,
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: `2px solid ${theme.palette.primary.main}`,
    },
  },
}));

const SendButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.secondary.contrastText,
  width: 48,
  height: 48,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}));

export interface ChatInputHandle {
  focus: () => void;
}

const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>((props, ref) => {
  const { onSendMessage, disabled, showStopButton, onStop } = props;
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }));

  const handleSend = () => {
    if (message.trim()) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <InputContainer>
      <StyledTextField
        fullWidth
        placeholder={t('chat.input.placeholder')}
        multiline
        maxRows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyPress={handleKeyPress}
        disabled={disabled}
        inputRef={inputRef}
        sx={{ flex: 1 }}
      />
      {showStopButton ? (
        <SendButton
          onClick={onStop}
          color="error"
          size="large"
          sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}
        >
          <AnimatedStopIcon />
        </SendButton>
      ) : (
        <SendButton
          onClick={handleSend}
          disabled={!message.trim() || disabled}
          size="large"
        >
          <Send />
        </SendButton>
      )}
    </InputContainer>
  );
});

ChatInput.displayName = 'ChatInput';

export default ChatInput;
